import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Departement, Immobilisation } from '../../models/models';
import {
  createOrUpdateImmobilisation,
  fetchImmobilisations,
} from '../../reduxStore/ImmobilisationSlice';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import SelectDepartement from '../Selects/SelectDepartement';

interface ImmobilisationFormProps {
  selectedImmobilisation?: Immobilisation | null;
  onClose?: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): Partial<Immobilisation> => ({
  id: -1,
  libelle: '',
  valeur_acquisition: 0,
  date_acquisition: today(),
  duree_mois: 60,
  methode: 'LINEAIRE',
  date_cession: null,
  valeur_cession: null,
});

const ImmobilisationForm: React.FC<ImmobilisationFormProps> = ({
  selectedImmobilisation,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters } = useSelector((state: RootState) => state.ImmobilisationSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const [form, setForm] = useState<Partial<Immobilisation>>(emptyForm());
  const [hasCession, setHasCession] = useState(false);
  // Département ciblé par l'action en cours (création ou édition), utilisé
  // uniquement en mode "Tous départements" (sinon le contexte actif s'applique).
  const [departement, setDepartement] = useState<Departement | null>(null);

  useEffect(() => {
    if (selectedImmobilisation) {
      const formatDate = (d: string | null | undefined) =>
        d ? new Date(d).toISOString().slice(0, 10) : '';
      setForm({
        id: selectedImmobilisation.id,
        libelle: selectedImmobilisation.libelle,
        valeur_acquisition: Number(selectedImmobilisation.valeur_acquisition),
        date_acquisition: formatDate(selectedImmobilisation.date_acquisition),
        duree_mois: selectedImmobilisation.duree_mois,
        methode: selectedImmobilisation.methode ?? 'LINEAIRE',
        date_cession: formatDate(selectedImmobilisation.date_cession) || null,
        valeur_cession: selectedImmobilisation.valeur_cession ?? null,
        document_vente_id: selectedImmobilisation.document_vente_id ?? null,
        ligne_document_id: selectedImmobilisation.ligne_document_id ?? null,
      });
      // Toujours repartir du departement_id RÉEL de l'entité (jamais du contexte
      // de session) — sinon une édition en mode "Tous" écraserait silencieusement
      // un bien déjà rattaché à un département avec NULL (bien "partagé").
      setDepartement(
        selectedImmobilisation.departement_id != null
          ? ({ id: selectedImmobilisation.departement_id } as Departement)
          : null
      );
      setHasCession(Boolean(selectedImmobilisation.date_cession));
    } else {
      setForm(emptyForm());
      setDepartement(null);
      setHasCession(false);
    }
  }, [selectedImmobilisation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' && value !== '' ? Number(value) : value === '' ? null : value,
    }));
  };

  const handleCessionToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setHasCession(checked);
    if (!checked) {
      setForm(prev => ({ ...prev, date_cession: null, valeur_cession: null }));
    } else if (!form.date_cession) {
      setForm(prev => ({ ...prev, date_cession: today() }));
    }
  };

  // Département actif → comportement inchangé (rattaché au contexte courant).
  // Mode "Tous" → valeur choisie explicitement (ou null = bien partagé).
  const departementId = selectedDepartement ? selectedDepartement.id : (departement?.id ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Immobilisation = {
      ...(form as Immobilisation),
      libelle: (form.libelle ?? '').trim(),
      valeur_acquisition: Number(form.valeur_acquisition),
      duree_mois: Number(form.duree_mois),
      date_cession: hasCession ? form.date_cession ?? null : null,
      valeur_cession: hasCession ? (form.valeur_cession != null ? Number(form.valeur_cession) : null) : null,
      departement_id: departementId,
    };

    dispatch(createOrUpdateImmobilisation(payload))
      .unwrap()
      .then(() => {
        dispatch(fetchImmobilisations(filters));
        if (onClose) onClose();
      })
      .catch(err => {
        console.error('Erreur enregistrement immobilisation:', err);
      });
  };

  const isFromInvoice = !!selectedImmobilisation?.ligne_document_id;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 600, mx: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="h6">
        {selectedImmobilisation ? "Modifier l'immobilisation" : 'Nouvelle immobilisation'}
      </Typography>

      {isFromInvoice && (
        <Typography variant="caption" color="warning.main">
          Cette immobilisation provient d'une facture fournisseur — le libellé, la valeur et la
          date d'acquisition sont gérés depuis la facture.
        </Typography>
      )}

      {!selectedDepartement && (
        <SelectDepartement
          onDepartementChange={setDepartement}
          selectedDepartement={departement}
          allowAll
          allLabel="Partagé (tous départements)"
        />
      )}

      <TextField
        label="Libellé"
        name="libelle"
        value={form.libelle ?? ''}
        onChange={handleChange}
        required
        disabled={isFromInvoice}
      />

      <TextField
        label="Valeur d'acquisition (TTC)"
        name="valeur_acquisition"
        type="number"
        value={form.valeur_acquisition ?? ''}
        onChange={handleChange}
        required
        disabled={isFromInvoice}
        inputProps={{ min: 0, step: '0.01' }}
      />

      <TextField
        label="Date d'acquisition"
        name="date_acquisition"
        type="date"
        value={form.date_acquisition ?? ''}
        onChange={handleChange}
        required
        disabled={isFromInvoice}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Durée d'amortissement (mois)"
        name="duree_mois"
        type="number"
        value={form.duree_mois ?? ''}
        onChange={handleChange}
        required
        inputProps={{ min: 1, step: 1 }}
      />

      <FormControlLabel
        control={<Checkbox checked={hasCession} onChange={handleCessionToggle} />}
        label="Bien cédé"
      />

      {hasCession && (
        <>
          <TextField
            label="Date de cession"
            name="date_cession"
            type="date"
            value={form.date_cession ?? ''}
            onChange={handleChange}
            required={hasCession}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Valeur de cession"
            name="valeur_cession"
            type="number"
            value={form.valeur_cession ?? ''}
            onChange={handleChange}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </>
      )}

      <Button type="submit" variant="contained">
        {selectedImmobilisation ? 'Mettre à jour' : 'Créer'}
      </Button>
    </Box>
  );
};

export default ImmobilisationForm;
